import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes, FaPaperPlane, FaBrain } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useLang } from "../hooks/useLang";
import { useTheme } from "../hooks/useTheme";
import logo from "@assets/logoMindbridge_1776100737645.png";

const GOLD = "#C9A84C";
const DARK = "#2A2E3A";

interface Message {
  id: number;
  role: "bot" | "user";
  text: string;
}

const SYSTEM_PROMPT =
  "Tu es l'assistant IA de MindBridge, une startup EdTech innovante spécialisée en IA, psychométrie et neurosciences. " +
  "Tu accompagnes les entreprises, organismes de formation et écoles. " +
  "Réponds dans la langue de l'utilisateur (FR/AR/EN). Sois professionnel, chaleureux et concis. " +
  "Si l'utilisateur veut être contacté, donne: +212.661.71.06.94 et contact@mind-bridge.education"+"Sois professionnel, chaleureux et clair"+
"Réponses COURTES (max 3 à 4 phrases)"+
"Va directement à l’essentiel"+
"Évite les explications longues sauf si l'utilisateur demande des détails";

export default function ChatBot() {
  const { i18n } = useTranslation();
  const { currentLang } = useLang();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const isRTL = currentLang === "ar";

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<{ role: string; content: string }[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const greeting =
    i18n.language === "ar" ? "مرحباً! 👋 أنا مساعد MindBridge الذكي. كيف يمكنني مساعدتك اليوم؟"
    : i18n.language === "en" ? "Hello! 👋 I'm MindBridge's AI assistant. How can I help you today?"
    : "Bonjour ! 👋 Je suis l'assistant IA de MindBridge. Comment puis-je vous aider ?";

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ id: 1, role: "bot", text: greeting }]);
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userText = input.trim();
    const userMsg: Message = { id: Date.now(), role: "user", text: userText };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    const newHistory = [...history, { role: "user", content: userText }];
    setHistory(newHistory);

    try {
      const apiKey = import.meta.env.VITE_CEREBRAS_API_KEY;
      if (!apiKey) throw new Error("No API key");

      const res = await fetch("https://api.cerebras.ai/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: "llama3.1-8b",
          max_tokens: 512,
          messages: [
            { role: "system", content: SYSTEM_PROMPT },
            ...newHistory,
          ],
        }),
      });

      const data = await res.json();
      const botText = data.choices?.[0]?.message?.content ?? fallbackReply(userText);
      const botMsg: Message = { id: Date.now() + 1, role: "bot", text: botText };
      setMessages((prev) => [...prev, botMsg]);
      setHistory((prev) => [...prev, { role: "assistant", content: botText }]);
    } catch {
      const fallback = fallbackReply(userText);
      setMessages((prev) => [...prev, { id: Date.now() + 2, role: "bot", text: fallback }]);
    } finally {
      setLoading(false);
    }
  };

  const fallbackReply = (msg: string): string => {
    const m = msg.toLowerCase();
    if (i18n.language === "ar") {
      if (m.includes("تدريب") || m.includes("تعلم")) return "نقدم برامج تدريبية متقدمة تعتمد على الذكاء الاصطناعي. تواصل معنا!";
      if (m.includes("سعر") || m.includes("تكلفة")) return "تختلف الأسعار حسب احتياجاتكم. اتصلوا بنا على +212.661.71.06.94";
      return "شكراً! للمزيد تواصل معنا: contact@mind-bridge.education أو +212.661.71.06.94";
    }
    if (i18n.language === "en") {
      if (m.includes("training") || m.includes("learn")) return "We offer AI-powered training programs. Contact us to learn more!";
      if (m.includes("price") || m.includes("cost")) return "Pricing varies by need. Call us at +212.661.71.06.94";
      return "Thank you! For more info: contact@mind-bridge.education or +212.661.71.06.94";
    }
    if (m.includes("formation") || m.includes("apprentissage")) return "Nous proposons des formations IA avancées. Contactez-nous !";
    if (m.includes("prix") || m.includes("tarif")) return "Les tarifs varient selon vos besoins. Appelez-nous au +212.661.71.06.94";
    return "Merci pour votre message ! Contactez-nous : contact@mind-bridge.education ou +212.661.71.06.94";
  };

  const placeholder =
    i18n.language === "ar" ? "اكتب رسالتك..." : i18n.language === "en" ? "Type your message..." : "Votre message...";

  const chatBg = isDark ? "#1a1d26" : "#f0f2f6";

  return (
    <>
      {/* Trigger button — bottom-right */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        className="w-14 h-14 rounded-full shadow-2xl flex items-center justify-center relative"
        style={{
          position: "fixed",
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
          [isRTL ? "left" : "right"]: "max(16px, env(safe-area-inset-right, 0px))",
          zIndex: 50,
          background: DARK,
          border: `2px solid ${GOLD}60`,
        }}
        aria-label="Chat IA"
      >
        <span className="absolute inset-0 rounded-full animate-ping" style={{ background: `${GOLD}25`, animationDuration: "2.5s" }} />
        <FaBrain size={22} color={GOLD} />
      </motion.button>

      {/* Chat window — opens above the trigger button */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            style={{
              position: "fixed",
              bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
              [isRTL ? "left" : "right"]: "max(16px, env(safe-area-inset-right, 0px))",
              zIndex: 50,
              width: "min(380px, calc(100vw - 32px))",
              height: "min(620px, calc(100vh - 130px))",
              background: chatBg,
            }}
            dir={isRTL ? "rtl" : "ltr"}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ background: DARK }}>
              <div className="flex items-center gap-2">
                <img src={logo} alt="MindBridge" className="w-7 h-7 object-contain" />
                <span className="text-white font-bold text-sm">MindBridge AI</span>
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              </div>
              <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white transition-colors">
                <FaTimes size={15} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className="max-w-[82%] px-3 py-2 rounded-xl text-sm leading-relaxed"
                    style={{
                      background: msg.role === "bot" ? (isDark ? DARK : "#e2e8f0") : GOLD,
                      color: msg.role === "bot" ? (isDark ? "#d1d5db" : "#1a1d26") : DARK,
                      borderRadius: msg.role === "bot" ? "4px 18px 18px 18px" : "18px 18px 4px 18px",
                    }}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              {/* Typing indicator */}
              {loading && (
                <div className="flex justify-start">
                  <div className="px-4 py-3 rounded-xl flex gap-1.5 items-center" style={{ background: isDark ? DARK : "#e2e8f0" }}>
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className="w-2 h-2 rounded-full"
                        style={{
                          background: GOLD,
                          animation: `bounce 1s ease-in-out ${i * 0.15}s infinite`,
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div
              className="p-3 flex gap-2 flex-shrink-0"
              style={{ borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}` }}
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder={placeholder}
                disabled={loading}
                className="flex-1 rounded-xl px-3 py-2 text-sm outline-none transition-colors"
                style={{
                  background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
                  color: isDark ? "#fff" : "#1a1d26",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                }}
              />
              <button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-opacity disabled:opacity-50"
                style={{ background: GOLD }}
              >
                <FaPaperPlane size={13} color={DARK} className={isRTL ? "rotate-180" : ""} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
